import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import { AB1FileResult } from '@/api/sequencingService';
import { Button, Tooltip, Typography, message, Modal, Input } from 'antd';
import { ZoomInOutlined, ZoomOutOutlined, DownloadOutlined, CopyOutlined } from '@ant-design/icons';
import { SANGER_COLORS, colorOfBase } from './sangerColors';

const { Text } = Typography;

// 颜色由共享模块提供：SANGER_COLORS

interface EnhancedSangerTraceChartProps {
  result: AB1FileResult;
}

const EnhancedSangerTraceChart: React.FC<EnhancedSangerTraceChartProps> = ({ result }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [zoomLevel, setZoomLevel] = useState<number>(1); // 默认缩放为1倍
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(800); // 默认宽度
  const [copyFallbackVisible, setCopyFallbackVisible] = useState(false);
  const copyTextRef = useRef<any>(null);

  // 监听容器宽度变化
  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.clientWidth);
      
      const resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          setContainerWidth(entry.contentRect.width);
        }
      });
      
      resizeObserver.observe(containerRef.current);
      
      return () => {
        if (containerRef.current) {
          resizeObserver.unobserve(containerRef.current);
        }
      };
    }
  }, []);

  // 可靠的复制函数：在非安全上下文回退到 execCommand
  const copyToClipboard = async (text: string) => {
    try {
      if (navigator.clipboard && (window as any).isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (_) {
      // fallthrough to legacy path
    }

    // 旧方案回退
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      return ok;
    } catch {
      return false;
    }
  };

  const mapRawIndexToBasePosition = (rawIndex: number, peak_positions: number[]): number => {
    const numBases = peak_positions.length;
    const upper = d3.bisectLeft(peak_positions, rawIndex);

    if (upper === 0) {
      if (peak_positions[0] > 0) {
        return (rawIndex / peak_positions[0]) - 1;
      }
      return -1;
    }

    if (upper >= numBases) {
      const lastPeakIndex = numBases - 1;
      const lastPeakPos = peak_positions[lastPeakIndex];
      const secondLastPeakPos = lastPeakIndex > 0 ? peak_positions[lastPeakIndex - 1] : 0;
      const dist = lastPeakPos - secondLastPeakPos;

      if (dist > 0) {
          return lastPeakIndex + (rawIndex - lastPeakPos) / dist;
      }
      return lastPeakIndex;
    }

    const prevPeakPos = peak_positions[upper - 1];
    const nextPeakPos = peak_positions[upper];
    const prevBase = upper - 1;
    
    const progress = (nextPeakPos > prevPeakPos) ? (rawIndex - prevPeakPos) / (nextPeakPos - prevPeakPos) : 0;
    return prevBase + progress;
  };

  const redrawChart = () => {
    if (!chartRef.current) return;
    
    const { data } = result;
    const { sequence, channels, peak_positions, confidence_levels, qualities } = data;
    
    // 清空图表
    d3.select(chartRef.current).selectAll("*").remove();
    
    // 图表尺寸和边距
    const margin = { top: 100, right: 50, bottom: 80, left: 50 };
    
    // 关键修改：SVG宽度随缩放级别变化
    // 计算每个碱基的宽度（像素）- 与缩放级别成正比
    const baseWidth = zoomLevel * 10; // 每个碱基的像素宽度
    // 计算整个序列的宽度
    const svgWidth = Math.max(containerWidth - margin.left - margin.right, sequence.length * baseWidth);
    
    const chartHeight = 300 - margin.top - margin.bottom;
    const height = chartHeight + margin.top + margin.bottom;
    
    // 创建SVG - 宽度使用动态计算的宽度
    const svg = d3.select(chartRef.current)
      .append("svg")
      .attr("width", svgWidth + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .style("display", "block") // 确保SVG是块级元素，影响父容器宽度
      .append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`);
    
    // 计算可见区域 - 根据缩放级别调整
    const visibleBases = Math.ceil((containerWidth - margin.left - margin.right) / baseWidth);
    const startBase = 0;
    const endBase = Math.min(sequence.length, startBase + visibleBases);
    
    // 创建X比例尺 - 使用整个序列范围
    const xScale = d3.scaleLinear()
      .domain([0, sequence.length])
      .range([0, svgWidth]);
    
    // 计算原始数据的显示范围
    const startRawIdx = peak_positions[startBase] || 0;
    const endRawIdx = peak_positions[endBase - 1] || channels.A.length;
    
    // 找出通道数据的最大值
    const maxTraceValue = Math.max(
      d3.max(channels.A) || 0,
      d3.max(channels.C) || 0,
      d3.max(channels.G) || 0,
      d3.max(channels.T) || 0
    );

    const yScale = d3.scaleLinear()
      .domain([0, maxTraceValue * 1.2])
      .range([chartHeight, 0]);

    const lineGenerator = d3.line<number>()
      .x((d, i) => xScale(mapRawIndexToBasePosition(i, peak_positions)))
      .y(d => yScale(d));

    // 使用统一的颜色方案绘制峰图
    // 注意：这里需要确保每个通道使用正确的颜色
    svg.append("path")
      .datum(channels.G)
      .attr("fill", "none")
      .attr("stroke", SANGER_COLORS.G)
      .attr("stroke-width", 1.5)
      .attr("d", lineGenerator);
      
    svg.append("path")
      .datum(channels.A)
      .attr("fill", "none")
      .attr("stroke", SANGER_COLORS.A)
      .attr("stroke-width", 1.5)
      .attr("d", lineGenerator);
      
    svg.append("path")
      .datum(channels.T)
      .attr("fill", "none")
      .attr("stroke", SANGER_COLORS.T)
      .attr("stroke-width", 1.5)
      .attr("d", lineGenerator);
      
    svg.append("path")
      .datum(channels.C)
      .attr("fill", "none")
      .attr("stroke", SANGER_COLORS.C)
      .attr("stroke-width", 1.5)
      .attr("d", lineGenerator);

    // 添加Y轴
    const yAxis = d3.axisLeft(yScale);
    svg.append("g").call(yAxis);

    // 添加X轴
    const xAxis = d3.axisBottom(xScale)
      .ticks(Math.min(sequence.length, 100))
      .tickFormat(d => {
        const pos = Math.round(Number(d)) + 1;
        // 只显示位置1和10的倍数（10、20、30等）
        if (pos === 1 || pos % 10 === 0) {
          return `${pos}`;
        }
        return '';
      });
        
    svg.append("g")
      .attr("class", "x-axis")
      .attr("transform", `translate(0, ${chartHeight})`)
      .call(xAxis);

    // 创建碱基数据 - 使用整个序列
    const baseData = d3.range(0, sequence.length);
    
    const baseGroup = svg.append("g");

    // 添加质量分数条 - 整个序列
    baseGroup.selectAll(".quality-bar")
      .data(baseData)
      .enter()
      .append("rect")
      .attr("class", "quality-bar")
      .attr("x", d => xScale(d))
      .attr("y", -margin.top + 40)
      .attr("width", d => Math.max(0, xScale(d + 1) - xScale(d)))
      .attr("height", d => {
        const quality = qualities[d] || 0;
        return (quality / 60) * 20; // 最大高度为20px，最高质量为60
      })
      .attr("fill", d => {
        const quality = qualities[d] || 0;
        if (quality >= 30) return "lightgreen"; // 高质量
        if (quality >= 20) return "lightblue";  // 中等质量
        return "pink";                          // 低质量
      });

    // 添加碱基文本 - 整个序列
    const baseTexts = baseGroup.selectAll(".base-text")
      .data(baseData)
      .enter()
      .append("text")
      .attr("class", "base-text")
      .attr("x", d => xScale(d))
      .attr("y", -margin.top + 30)
      .attr("text-anchor", "middle")
      .attr("font-family", "monospace")
      .attr("font-size", "14px")
      .attr("fill", d => colorOfBase(sequence[d]))
      .text(d => sequence[d]);

    // 文件信息移至外部固定信息条显示

    // 添加交互高亮功能
    const highlightGroup = svg.append("g");
    const highlightLine = highlightGroup.append("line")
      .attr("stroke", "#ff9800")
      .attr("stroke-width", 1)
      .attr("y1", -margin.top)
      .attr("y2", chartHeight)
      .style("opacity", 0);
      
    const highlightText = highlightGroup.append("text")
      .attr("font-size", "12px")
      .attr("text-anchor", "middle")
      .attr("y", -margin.top + 80)
      .style("opacity", 0);

    const highlightRect = highlightGroup.append("rect")
      .attr("fill", "rgba(255, 152, 0, 0.1)")
      .attr("y", -margin.top)
      .attr("height", height)
      .style("opacity", 0);

    // 添加透明的交互层 - 覆盖整个图表宽度
    svg.append("rect")
      .attr("width", svgWidth)
      .attr("height", chartHeight)
      .attr("fill", "transparent")
      .on("mousemove", function(event) {
        const [mouseX] = d3.pointer(event);
        const baseIndex = Math.floor(xScale.invert(mouseX));
        
        if (baseIndex >= 0 && baseIndex < sequence.length) {
          const base = sequence[baseIndex];
          const quality = qualities[baseIndex] || 0;
          const peakPos = peak_positions[baseIndex];
          
          // 获取四个通道在该位置的信号强度
          const signalA = channels.A[peakPos] || 0;
          const signalC = channels.C[peakPos] || 0;
          const signalG = channels.G[peakPos] || 0;
          const signalT = channels.T[peakPos] || 0;
          
          // 计算总信号强度
          const totalSignal = signalA + signalC + signalG + signalT;
          
          // 高亮当前碱基
          baseTexts.attr("font-weight", (d, i) => d === baseIndex ? "bold" : "normal")
                   .attr("font-size", (d, i) => d === baseIndex ? "18px" : "14px");
          
          // 显示高亮线
          highlightLine
            .attr("x1", xScale(baseIndex))
            .attr("x2", xScale(baseIndex))
            .style("opacity", 1);
            
          // 显示高亮区域
          highlightRect
            .attr("x", xScale(baseIndex))
            .attr("width", xScale(baseIndex + 1) - xScale(baseIndex))
            .style("opacity", 0.5);
            
          // 显示信息文本
          highlightText
            .attr("x", xScale(baseIndex))
            .text(`位置: ${baseIndex + 1} | 碱基: ${base} | 质量分: ${quality} | 信号强度: ${Math.round(totalSignal)}`)
            .style("opacity", 1);
        }
      })
      .on("mouseleave", function() {
        // 移除高亮效果
        baseTexts.attr("font-weight", "normal")
                 .attr("font-size", "14px");
        highlightLine.style("opacity", 0);
        highlightRect.style("opacity", 0);
        highlightText.style("opacity", 0);
      });
      
    // 直接设置内层容器的宽度，确保与SVG宽度一致
    d3.select(chartRef.current).style("width", `${svgWidth + margin.left + margin.right}px`);
  };

  useEffect(() => {
    redrawChart();
  }, [result, zoomLevel]);

  const handleZoomIn = () => setZoomLevel(prev => prev * 1.5);
  const handleZoomOut = () => setZoomLevel(prev => prev / 1.5);

  const downloadSVG = () => {
    if (chartRef.current) {
      const svgEl = chartRef.current.querySelector('svg');
      if (svgEl) {
        const svgData = new XMLSerializer().serializeToString(svgEl);
        const blob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${result.filename}_trace.svg`;
        link.click();
        URL.revokeObjectURL(url);
      }
    }
  };

  // 重置缩放级别为1倍
  const handleResetZoom = () => setZoomLevel(1);

  const handleCopySequence = async () => {
    const ok = await copyToClipboard(result.data.sequence);
    if (ok) {
      message.success('序列已复制');
    } else {
      message.error('复制失败');
      setCopyFallbackVisible(true);
    }
  };

  const infoText = `${result.filename} | 序列长度: ${result.data.sequence.length} | 平均质量分: ${Math.round(d3.mean(result.data.qualities) || 0)}`;

  return (
    <div style={{ position: 'relative' }}>
      {/* 工具栏：始终可见 */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: '#fff',
          padding: '8px 12px',
          borderBottom: '1px solid #eee',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <div>
          <Tooltip title="放大">
            <Button icon={<ZoomInOutlined />} onClick={handleZoomIn} style={{ marginRight: 8 }} />
          </Tooltip>
          <Tooltip title="缩小">
            <Button icon={<ZoomOutOutlined />} onClick={handleZoomOut} style={{ marginRight: 8 }} />
          </Tooltip>
          <Button onClick={handleResetZoom} style={{ marginRight: 12 }}>重置缩放</Button>
          <Text>缩放: {zoomLevel.toFixed(2)}x</Text>
        </div>
        <div>
          <Tooltip title="复制序列">
            <Button icon={<CopyOutlined />} onClick={handleCopySequence} style={{ marginRight: 8 }} />
          </Tooltip>
          <Tooltip title="下载SVG格式图表">
            <Button icon={<DownloadOutlined />} onClick={downloadSVG}>下载SVG</Button>
          </Tooltip>
        </div>
      </div>

      {/* 居中信息条：始终位于可视区中间（横向不随滚动） */}
      <div
        style={{
          position: 'sticky',
          top: 44,
          zIndex: 9,
          width: '100%',
          textAlign: 'center',
          pointerEvents: 'none'
        }}
      >
        <span style={{
          display: 'inline-block',
          background: 'rgba(255,255,255,0.85)',
          padding: '6px 12px',
          border: '1px solid #e0e0e0',
          borderRadius: 6,
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
        }}>
          {infoText}
        </span>
      </div>

      {/* 横向滚动容器 */}
      <div ref={containerRef} style={{ overflowX: 'auto', maxWidth: '100%' }}>
        {/* 图表容器 */}
        <div
          ref={chartRef}
          style={{
            display: 'inline-block',
            width: 'auto',
            overflowX: 'visible',
            overflowY: 'hidden',
            border: '1px solid #eee',
            borderRadius: '4px',
            padding: '10px',
            height: '600px'
          }}
        />
      </div>

      {/* 复制失败回退弹窗 */}
      <Modal
        title="复制序列"
        open={copyFallbackVisible}
        onCancel={() => setCopyFallbackVisible(false)}
        okText="复制到剪贴板"
        onOk={async () => {
          const ok = await copyToClipboard(result.data.sequence);
          if (ok) {
            message.success('序列已复制');
            setCopyFallbackVisible(false);
          } else {
            message.error('复制仍然失败，请按 Ctrl/Cmd + C 手动复制');
            try { (copyTextRef.current as any)?.focus?.(); (copyTextRef.current as any)?.select?.(); } catch {}
          }
        }}
      >
        <Input.TextArea
          ref={copyTextRef}
          value={result.data.sequence}
          readOnly
          autoSize={{ minRows: 6, maxRows: 12 }}
          onFocus={(e) => e.currentTarget.select()}
        />
        <div style={{ marginTop: 8, color: '#999' }}>如果按钮复制失败，请选中后使用 Ctrl/Cmd + C 手动复制。</div>
      </Modal>
    </div>
  );
};

export default EnhancedSangerTraceChart; 